import { AdornisRouter } from '@adornis/base/client/a-router';
import { windowSize } from '@adornis/base/client/frontend-helpers/window-size';
import { ValueEvent } from '@adornis/base/utilTypes';
import { EntityData } from '@adornis/baseql/entities/adornisEntity';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element';
import { BehaviorController } from '@adornis/chemistry/controllers/behavior-controller';
import { DataLoadingController } from '@adornis/chemistry/controllers/data-loading-controller';
import { LocalStorageBehaviorController } from '@adornis/chemistry/controllers/local-storage-behavior-controller';
import { ObservableController } from '@adornis/chemistry/controllers/observable-controller';
import '@adornis/chemistry/elements/components/x-chip';
import { elevation } from '@adornis/chemistry/elements/theming/design';
import { ElementsDialog } from '@adornis/dialog/x-dialog';
import '@material/mwc-icon-button';
import chroma from 'chroma-js';
import { shorten } from 'client/main-element';
import { colors } from 'client/startup';
import { EventLog } from 'db/EventLog';
import { GoalAgg } from 'db/GoalAgg';
import { MoodleAttempts } from 'db/MoodleAttempts';
import { User } from 'db/User';
import { html, nothing } from 'lit';
import { state } from 'lit/decorators.js';
import { customElement } from 'lit/decorators/custom-element.js';
import { DateTime } from 'luxon';
import { Aggregator } from 'mingo';
import 'mingo/init/system';
import { map } from 'rxjs/internal/operators/map';
import { combineLatestWith } from 'rxjs/operators';
import { cap } from '../main-element';
import { FeedbackPop } from './feedback-pop';
import microtip from './microtip.css';

@customElement('table-view')
export abstract class TableView<T extends typeof MongoEntity> extends ChemistryLitElement {
  styles() {
    return {
      // ...microtip,
      '.header': {
        position: 'sticky',
        top: '0px',
        background: 'white',
        'z-index': '9',
        padding: '10px 0px',
      },
    };
  }
  public detailMapping = {
    taskQuestion: 'Frage',
    linkAccordeon: 'Antworten aller SuS',
    avgscore: 'Durchschnitt',
    taskType: 'Fragetyp',
    Nlp: 'Lernziele',
    goalId: 'Lernziel-Id',
    goalTitle: 'Lernziel',
    goalLevel: 'Lernziel-Typ',
    goalText: 'Beschreibung',
    taskIds: 'Aufgaben',
    essayLabels: 'Alle NLP-Scores',
  };
  abstract collection: T; // This is actually a type! T is a type of a type!
  abstract objectiveField: string;
  abstract objectiveName: string;
  abstract objectiveMeta: string[];

  @state() public usernameMapping = new LocalStorageBehaviorController(this, 'usernameMapping', [] as string[][]);
  @state() public courseSelected = new ObservableController(this, AdornisRouter.get().queryParams.get('course'));
  @state() public selectedUsers = new BehaviorController<string[]>(this, []);
  public tasksPerPage = new ObservableController(
    this,
    windowSize.pipe(
      // tap(size => console.log('size', size.width)),
      map(size => Math.min(12, Math.floor((size.width - 700) / 60))), // 400px margin, 70px per column
      // tap(x => console.log('tasks per page', x)),
    ),
  );
  @state() public goalSelected = new BehaviorController<{
    _id: string;
    goalTitle: string;
    goalText: string;
    goalLevel: number;
    avgscore: number;
    taskIds: number[];
  } | null>(this, null);

  public rawData = new DataLoadingController(this, () => this.collection.subscribeAll()(this.collection.allFields));
  public goalagg = new DataLoadingController(this, () => GoalAgg.subscribeAll()(GoalAgg.allFields));
  @state() public lastUpdate = new ObservableController(
    this,
    EventLog.lastUpdate(this.courseSelected.value)(EventLog.allFields),
  );
  @state() public orderTask = new BehaviorController<null | number>(this, null); // 0 means order by overall score, ie indexing starts at 1
  @state() public orderReverse = new BehaviorController(this, true);
  @state() public userFilter = new BehaviorController(this, [] as string[]);
  public asdfasdf = AdornisRouter.get().queryParams.subscribe(x => {
    //console.log('sort', x.get('sort'), this.orderTask.value, this.orderReverse.value);
    this.orderTask.next(0);
    if (x.get('sort') === 'asc') this.orderReverse.next(true);
    else if (x.get('sort') === 'desc') this.orderReverse.next(false);
    // console.log('sort', this.orderTask.value,this.orderReverse.value);
    this.userFilter.next(x.get('users')?.split(',') || []);
  });
  @state() public tasks = new ObservableController(
    this,
    this.rawData.observable.pipe(
      combineLatestWith(this.courseSelected.observable, this.goalSelected),
      map(([x, course, goal]) => {
        if (this.objectiveName === 'task')
          return (x as MoodleAttempts[]).filter(
            y => y.courseId === course && (goal === null ? true : y.goalIds.includes(Number(goal._id))),
          );
        else return (x as GoalAgg[]).filter(y => y.courseId === course);
      }),
      map(raw => {
        const attempts = raw.map(att => att.toJSON() as EntityData<T>);
        return new Aggregator([
          {
            $group: {
              _id: '$' + this.objectiveField,
              ...this.objectiveMeta.reduce((acc, field) => ({ ...acc, [field]: { $first: '$' + field } }), {}),
              avgscore: {
                $avg: {
                  $ifNull: ['$score', 0],
                },
              }, // { $avg: '$score' },
            },
          },
        ])
          .run(attempts)
          .sort((a, b) => a._id - b._id) as Array<{ _id: string; avgscore: number }>;
      }),
    ),
  );
  // Only needed for filtering by goals in the task view!
  @state() public allGoals = new ObservableController(
    this,
    this.goalagg.observable.pipe(
      combineLatestWith(this.courseSelected.observable),
      map(([x, course]) => x.filter(y => y.courseId === course)),
      map(raw => {
        const attempts = raw.map(att => att.toJSON() as EntityData<GoalAgg>);
        return new Aggregator([
          {
            $group: {
              _id: '$goalId',
              ...['goalTitle', 'goalText', 'goalLevel'].reduce(
                (acc, field) => ({ ...acc, [field]: { $first: '$' + field } }),
                {},
              ),
              avgscore: {
                $avg: {
                  $ifNull: ['$score', 0],
                },
              }, // { $avg: '$score' },
            },
          },
        ])
          .run(attempts)
          .sort((a, b) => a._id - b._id) as Array<{
          _id: string;
          goalTitle: string;
          goalText: string;
          goalLevel: string;
          avgscore: number;
        }>;
      }),
    ),
  );
  @state() public toggleHigher = new BehaviorController<Boolean>(this, true);
  @state() public displayTasks = new ObservableController(
    this,
    this.tasks.observable.pipe(
      combineLatestWith(this.toggleHigher),
      map(([tasks, more]) => {
        if (this.objectiveField === 'task' || more) return tasks;
        return tasks;
      }),
    ),
  );

  @state() public taskcount = new ObservableController(
    this,
    this.displayTasks.observable.pipe(map(disp => [...new Set(disp.map(t => t['_id']))].length)),
  );

  @state() public higherGoalCount = new ObservableController(
    this,
    this.tasks.observable.pipe(
      map(tasks => {
        if (this.objectiveField === 'task') return null;
        return tasks.filter(task => Number(task.goalLevel) === 1).length;
      }),
    ),
  );
  @state() public users = new ObservableController(
    this,
    this.rawData.observable.pipe(
      combineLatestWith(this.courseSelected.observable, this.goalSelected),
      map(([x, course, goal]) => {
        if (this.objectiveName === 'task')
          return (x as MoodleAttempts[]).filter(
            y => y.courseId === course && (goal === null ? true : y.goalIds.includes(Number(goal._id))),
          );
        else return (x as GoalAgg[]).filter(y => y.courseId === course);
      }),
      combineLatestWith(this.orderTask, this.orderReverse, this.userFilter),
      map(([raw, ordertask, orderreverse, uFilter]) => {
        const att = raw.map(att => att.toJSON()) as Array<EntityData<T>>;
        const agg = new Aggregator([
          {
            $group: {
              _id: '$userId',
              userName: { $first: '$userName' },
              pseudoRealname: { $first: '$pseudoRealname' },
              // avgscore: {
              //   $avg: {
              //     $ifNull: ['$score', 0],
              //   },
              // },
              avgscore: {
                $divide: [
                  {
                    $sum: {
                      $ifNull: ['$score', 0],
                    },
                  },
                  this.taskcount.value,
                ],
              },
              // { $avg: '$score' },
              maxscore: { $max: '$score' },
              items: { $push: '$$CURRENT' },
              orderScore: {
                $function: {
                  body: (userId: string) => {
                    if (!ordertask) return;
                    return att.find(doc => doc.userId === userId[0] && doc[this.objectiveField] == String(ordertask))
                      ?.score;
                  },
                  args: ['$userId'],
                  lang: 'js',
                },
              },
            },
          },
          {
            $addFields: {
              maxscoreinfo: {
                $map: {
                  input: {
                    $filter: {
                      input: '$items',
                      as: 'i',
                      cond: { $eq: ['$$i.score', '$maxscore'] },
                    },
                  },
                  as: 'bestGoal',
                  in: '$$bestGoal',
                },
              },
            },
          },
        ]).run(att) as Array<{
          _id: string;
          userName: string;
          pseudoRealname: string;
          avgscore: number;
          orderScore: number | null;
          maxscore: number;
          items: Array<any>;
          maxscoreinfo: Array<EntityData<T>>;
        }>;
        const sorted = agg.sort(function (x, y) {
          const reverse = orderreverse ? -1 : 1;
          if (ordertask === null) return x.pseudoRealname.localeCompare(y.pseudoRealname) * reverse;
          if (ordertask === 0) {
            return (y.avgscore - x.avgscore) * reverse;
          }
          const a = x.orderScore;
          const b = y.orderScore;
          return isNaN(a) - isNaN(b) || (b - a) * reverse;
        });
        if (uFilter.length) return sorted.filter(x => uFilter.includes(String(x._id)));
        return sorted;
      }),
    ),
  );

  @state() public fullRecord = new ObservableController(
    this,
    this.rawData.observable.pipe(
      combineLatestWith(this.courseSelected.observable, this.goalSelected),
      map(([x, course, goal]) => {
        if (this.objectiveName === 'task')
          return (x as MoodleAttempts[]).filter(
            y => y.courseId === course && (goal === null ? true : y.goalIds.includes(Number(goal._id))),
          );
        else return (x as GoalAgg[]).filter(y => y.courseId === course);
      }),
      map(raw => {
        // THIS STUFF COULD AND SHOULD BE DONE AS A MONGO AGGREGATION -- Construct attempts documents from raw data and produce the marginal datasets using Mingo
        const attempts = raw.map(att => att.toJSON() as EntityData<T>);
        const distusers = new Aggregator([{ $group: { _id: '$userId' } }])
          .run(attempts)
          .map(a => a._id)
          .sort((a, b) => a - b) as Array<{ userId: string }>;
        const disttasks = new Aggregator([{ $group: { _id: '$' + this.objectiveField } }])
          .run(attempts)
          .map(a => a._id)
          .sort((a, b) => a - b) as Array<{ [key: string]: any }>;
        // (Probably cheaper) alternative:
        // const distusers = [...new Set(goalagg.map(att => att.userId))];
        // const distgoals = [...new Set(goalagg.map(att => att.goalId))];
        // console.log(userIds, taskIds, 'USER & TASK IDS');

        // Construct a two-way hashtable for most efficient memory access
        const tasksEmpty = disttasks.reduce((a, c) => {
          return { ...a, [c]: { score: null, type: null, title: null } };
        }, {}) as Array<Number>;
        const fullrecord = Object.fromEntries(distusers.map(user => [user, JSON.parse(JSON.stringify(tasksEmpty))]));

        // console.log(fullrecord, 'USERRECORD EMPTY');

        attempts.forEach(att => {
          const u = att.userId; // user id
          const t = att[this.objectiveField]; // task id
          fullrecord[u]![t]! = att;
        });
        return fullrecord;
      }),
    ),
  );

  @state() public totalPages = new ObservableController(
    this,
    this.taskcount.observable.pipe(
      combineLatestWith(this.tasksPerPage.observable),
      map(([taskcount, perpage]) => Math.ceil(taskcount / perpage)),
    ),
  );
  @state() public displayPage = new BehaviorController(this, 1);
  @state() public startTaskIndex = new ObservableController(
    this,
    this.displayPage.pipe(
      combineLatestWith(this.tasksPerPage.observable),
      map(([page, perpage]) => perpage * (page - 1)),
    ),
  );
  @state() public endTaskIndex = new ObservableController(
    this,
    this.displayPage.pipe(
      combineLatestWith(this.taskcount.observable, this.totalPages.observable, this.tasksPerPage.observable),
      map(([page, taskcount, totalPages, perpage]) =>
        page != totalPages ? 0 + perpage * page : 0 + perpage * (page - 1) + (taskcount % perpage),
      ),
    ),
  );
  @state() public realnames = new DataLoadingController(this, () => User.subscribeAll<User>()(User.allFields));

  render() {
    console.log('TASKsss', this.tasks.value);
    // console.log(
    //   'SORTING',
    //   this.tasks.value?.find(x => x._id == String(this.orderTask.value)),
    // );
    // console.log('SORTING', this.tasks.value?.find(x => x._id == String(this.orderTask.value))?.['goalTitle']);
    console.log('FULL RECORD', this.fullRecord.value);
    // console.log('ALL GOALS', this.allGoals.value);
    // console.log('Course selected', this.courseSelected.value);
    // console.log('USERS', this.users.value);
    // console.log('DISPLAY TASKS', this.displayTasks.value);
    console.log('TASKS', this.tasks.value);
    console.log('TASKCOUNT', this.taskcount.value);
    console.log('HIGHER GOAL COUNT', this.higherGoalCount.value);
    console.log('this.startTaskIndex', this.startTaskIndex.value);
    console.log('this.endTaskIndex', this.endTaskIndex.value);
    console.log(
      Math.min(this.tasks.value?.length - this.higherGoalCount.value, this.endTaskIndex.value) -
        this.startTaskIndex.value,
    );
    console.log('USERFILTER', this.userFilter.value);
    // console.log(
    //   'CALC',
    //   Math.max(
    //     0,
    //     Math.min((this.tasks.value?.length || 0) - (this.higherGoalCount.value || 0), this.endTaskIndex.value || 0) -
    //       (this.startTaskIndex.value || 0),
    //   ),
    //   Math.max(
    //     0,
    //     (this.endTaskIndex.value || 0) -
    //       (this.startTaskIndex.value || 0) -
    //       Math.max(
    //         0,
    //         Math.min(
    //           (this.tasks.value?.length || 0) - (this.higherGoalCount.value || 0),
    //           this.endTaskIndex.value || 0,
    //         ) - (this.startTaskIndex.value || 0),
    //       ),
    //   ),
    // );

    if (!this.fullRecord.value)
      return html` <x-flex space="lg" crossaxis-center center style="height:90vh;"> <x-loader></x-loader> </x-flex> `;
    else
      return html`
        <style>
          ${microtip}
        </style>
        <x-flex space="md" style="padding: 0px 0px" crossaxis-center center>
          <x-flex style="height:50px;"></x-flex>

          <x-h2 style="text-transform: none; padding:bottom:15px"
            >Scores der SuS nach
            ${this.objectiveName == 'goal' ? 'Lernzielen' : this.objectiveName === 'task' ? 'Aufgaben' : ''}
          </x-h2>
          <x-text>
            ${this.orderTask.value === null
              ? html`<b>Alphabetisch</b>`
              : !this.orderReverse.value
              ? html`<b>Absteigend</b>`
              : html`<b>Aufsteigend</b>`}
            sortiert nach
            ${this.orderTask.value === null
              ? html`<b>Namen</b>`
              : this.orderTask.value === 0
              ? html`<b>Gesamtpunktzahl</b>`
              : html`<b>
                  ${this.objectiveName === 'task'
                    ? 'Task ' + this.tasks.value?.find(x => x._id == String(this.orderTask.value))?.['moodleTaskId']
                    : 'Lernziel: ' +
                      this.tasks.value?.find(x => x._id == String(this.orderTask.value))?.['goalTitle']}</b
                >`}
          </x-text>

          <!-- META -->
          <!-- <x-flex horizontal space="lg"> -->
          <!-- <x-text>
              Displaying ${this.objectiveName}s ${1 + this.startTaskIndex.value!} - ${this.endTaskIndex.value} of
              ${this.taskcount.value}
            </x-text> -->
          <!-- <x-text>Page ${this.displayPage.value} of ${this.totalPages.value}</x-text>
            <x-text>Max per page ${this.tasksPerPage.value}</x-text>
            <x-text
              >Order by ${this.objectiveName}
              ${this.orderTask.value ?? 'user name'}${this.orderReverse.value ? ' ascendingly' : ' descendingly'}
            </x-text> -->
          <!-- </x-flex> -->

          <!-- COLUMN HEADERS -->
          <x-flex
            style="grid-column: 1/-1; border-radius: 3px; box-shadow: ${elevation[2]}; position: sticky; top: 75px; gap:20px;"
            horizontal
            class="header"
            space-between
          >
            <!-- User header -->
            <x-flex horizontal crossaxis-center style="width:150px;">
              <mwc-checkbox
                style="--mdc-theme-secondary: ${this.colors.accent}"
                id="allbox"
                @input=${e => {
                  if (!this.users.value) return;
                  if (e.target.checked) this.selectedUsers.next([]);
                  else {
                    const users = Object.entries(this.users.value).map(([i, v]) => v._id);
                    this.selectedUsers.next(users);
                  }
                }}
              ></mwc-checkbox>
              <x-text style="margin-right:15px; font-size:14pt;">Schüler*in</x-text>
              <mwc-icon-button
                style="font-size:14pt; cursor:pointer; --mdc-icon-button-size:20px;"
                @click=${() => {
                  this.orderTask.value === null
                    ? this.orderReverse.next(!this.orderReverse.value)
                    : this.orderTask.next(null);
                }}
                icon=${this.orderTask.value === null && !this.orderReverse.value
                  ? 'keyboard_arrow_down'
                  : this.orderTask.value === null && this.orderReverse.value
                  ? 'keyboard_arrow_up'
                  : 'unfold_more'}
              >
              </mwc-icon-button>
            </x-flex>

            <!-- Objective headers -->
            <x-flex>
              ${this.objectiveName === 'goal'
                ? html`<x-flex horizontal style="margin-bottom:10px;">
                    <x-flex
                      data-microtip-position="top"
                      role="tooltip"
                      aria-label="Fachwissen & Fähigkeiten"
                      style="margin:5px; background-color:#c2bbff; width: ${((this.objectiveName === 'goal'
                        ? 100
                        : 20) +
                        20) *
                        Math.max(
                          0,
                          Math.min(
                            (this.tasks.value?.length || 0) - (this.higherGoalCount.value || 0),
                            this.endTaskIndex.value || 0,
                          ) - (this.startTaskIndex.value || 0),
                        ) -
                      10}px; height: 5px;"
                    >
                    </x-flex>

                    <x-flex
                      data-microtip-position="top"
                      role="tooltip"
                      aria-label="Kompetenzziele"
                      style="margin:5px; background-color: #9083ff; width: ${Number(this.toggleHigher.value) *
                        ((this.objectiveName === 'goal' ? 100 : 20) + 20) *
                        Math.max(
                          0,
                          (this.endTaskIndex.value || 0) -
                            (this.startTaskIndex.value || 0) -
                            Math.max(
                              0,
                              Math.min(
                                (this.tasks.value?.length || 0) - (this.higherGoalCount.value || 0),
                                this.endTaskIndex.value || 0,
                              ) - (this.startTaskIndex.value || 0),
                            ),
                        ) -
                      10}px; height: 5px;"
                    >
                    </x-flex>
                  </x-flex>`
                : nothing}

              <x-flex horizontal space-between style="gap:20px;">
                ${this.displayTasks.value?.slice(this.startTaskIndex.value, this.endTaskIndex.value).map(
                  task => html` <x-flex
                    space="sm"
                    horizontal
                    style="flex-wrap: wrap; max-width:${this.objectiveName === 'goal' ? '100px' : '70px'};"
                    center
                  >
                    <x-flex
                      crossaxis-center
                      style="white-space:nowrap; width: 100%;"
                      space="sm"
                      data-microtip-position="bottom"
                      role="tooltip"
                      data-microtip-size="large"
                      aria-label="${this.objectiveName === 'task'
                        ? 'Quiz:  ' +
                            task.quizName +
                            '\n \n' +
                            'Frage:  \n' +
                            task.taskQuestion +
                            ' \n \n' +
                            'Fragetyp:  ' +
                            {
                              gapselect: ' Lückentext',
                              essay: ' Freitext',
                              multianswer: ' Multiple Choice',
                              truefalse: ' Multiple Choice',
                            }[task.taskType] || ''
                        : task.goalTitle + '\n \n' + task.goalText}"
                    >
                      <x-text
                        style="font-size:${this.objectiveName === 'goal'
                          ? '12pt'
                          : '15pt'}; text-transform:capitalize; cursor:pointer;"
                        @click=${() =>
                          ElementsDialog.alert(html` <x-flex crossaxis-center space="sm">
                            <x-h1>
                              <!-- ${cap(this.objectiveName)} -->
                              ${this.objectiveName === 'task'
                                ? task.quizName + ' - ' + 'Task ' + Number(task['moodleTaskId'])
                                : 'Lernziel: ' + task['goalTitle']}
                            </x-h1>
                            <x-grid columns="2" style="grid-template-columns:auto auto; grid-gap:15px;">
                              ${Object.entries(task).map(([i, v]) =>
                                Object.keys(this.detailMapping).includes(i)
                                  ? html`<x-text style="text-align:right; white-space:text-wrap;">
                                        ${Object.keys(this.detailMapping).includes(i) ? this.detailMapping[i] : i}:
                                      </x-text>
                                      ${typeof v === 'string' && v.startsWith('http')
                                        ? html`<x-link href=${v}>
                                            <x-text style="color:blue; overflow-wrap: anywhere;">${v}</x-text>
                                          </x-link>`
                                        : html`<x-text
                                            >${Array.isArray(v)
                                              ? v.join(', ')
                                              : i == 'taskType'
                                              ? {
                                                  gapselect: 'Lückentext',
                                                  essay: 'Freitext',
                                                  multianswer: 'Multiple Choice)',
                                                  truefalse: 'falsch/richtig',
                                                }[v]
                                              : i == 'avgscore'
                                              ? (v * 100).toFixed() + '%'
                                              : v}</x-text
                                          >`}`
                                  : '',
                              )}
                            </x-grid>
                          </x-flex>`)}
                      >
                        ${this.objectiveName === 'task' ? 'Task ' : ''}${this.objectiveName === 'task'
                          ? Number(task['moodleTaskId'])
                          : shorten(task['goalTitle'], 11)}
                      </x-text>
                      <x-text> Avg: ${task.avgscore === null ? 'N/A' : (task.avgscore * 100).toFixed() + `%`} </x-text>
                    </x-flex>
                    <mwc-icon-button
                      style="font-size:15pt; cursor:pointer; --mdc-icon-button-size:20px;"
                      @click=${() => {
                        this.orderTask.value === Number(task._id)
                          ? this.orderReverse.next(!this.orderReverse.value)
                          : this.orderTask.next(Number(task._id));
                      }}
                      icon=${this.orderTask.value == task._id && !this.orderReverse.value
                        ? 'keyboard_arrow_down'
                        : this.orderTask.value == task._id && this.orderReverse.value
                        ? 'keyboard_arrow_up'
                        : 'unfold_more'}
                    >
                    </mwc-icon-button>
                    <x-icon
                      style="cursor: pointer; visibility:${task.avgscore >= 0 && task.avgscore <= 0.3 && task !== null
                        ? 'visible'
                        : 'hidden'}; color:${colors.red}; font-size:15pt;"
                      data-microtip-position="bottom"
                      role="tooltip"
                      data-microtip-size="medium"
                      aria-label="In ${`${
                        this.objectiveName === 'goal'
                          ? `Lernziel "${task['goalTitle']}"`
                          : 'Aufgabe ' + parseInt(task['moodleTaskId'])
                      }  hat die Klasse einen durchschnittlichen Score von unter 30%!`}"
                    >
                      warning
                    </x-icon>
                  </x-flex>`,
                )}</x-flex
              >
            </x-flex>
            <!-- Last column -->
            <x-flex center horizontal crossaxis-center style="width:100px; ">
              <x-text style="font-size:14pt; height: 15px;">Alle ${this.objectiveName}s</x-text>
              <mwc-icon-button
                style="font-size:15pt; cursor:pointer; --mdc-icon-button-size:20px;"
                @click=${() => {
                  this.orderTask.value === 0
                    ? this.orderReverse.next(!this.orderReverse.value)
                    : this.orderTask.next(Number(0));
                }}
                icon=${this.orderTask.value === 0 && !this.orderReverse.value
                  ? 'keyboard_arrow_down'
                  : this.orderTask.value === 0 && this.orderReverse.value
                  ? 'keyboard_arrow_up'
                  : 'unfold_more'}
              >
              </mwc-icon-button>
            </x-flex>
          </x-flex>

          <!-- MAIN CHART -->
          <x-grid
            style="grid-gap: 6px 20px; grid-template-columns: 150px repeat(${String(
              this.endTaskIndex.value! - this.startTaskIndex.value!,
            )}, ${this.objectiveName === 'goal' ? '100px' : '70px'}) 100px;"
          >
            <!-- TABLE ROWS -->
            ${this.users.value?.map(
              user => html`
                <!-- User -->
                <x-flex horizontal crossaxis-center>
                  <mwc-checkbox
                    style="--mdc-theme-secondary: ${this.colors.accent}"
                    id=${'checkbox-' + user._id}
                    ?checked=${this.selectedUsers.value.includes(user._id)}
                    @input=${e => {
                      const currentSelected = this.selectedUsers.value;
                      if (currentSelected.includes(user._id)) {
                        currentSelected.splice(currentSelected.indexOf(user._id), 1);
                        this.selectedUsers.next(currentSelected);
                      } else this.selectedUsers.next([...currentSelected, user._id]);
                    }}
                  >
                  </mwc-checkbox>
                  <x-text
                    style="cursor: pointer;"
                    @click=${e => {
                      const currentSelected = this.selectedUsers.value;
                      if (currentSelected.includes(user._id)) {
                        currentSelected.splice(currentSelected.indexOf(user._id), 1);
                        this.selectedUsers.next(currentSelected);
                      } else this.selectedUsers.next([...currentSelected, user._id]);
                    }}
                  >
                    ${shorten(
                      this.usernameMapping.value?.find(x => x[0] === user.userName)?.[2] || user.pseudoRealname,
                    )}
                  </x-text>
                </x-flex>

                <!-- Objective entries -->
                ${this.displayTasks.value?.slice(this.startTaskIndex.value, this.endTaskIndex.value).map(task => {
                  const att = this.fullRecord.value[user._id][task._id] as EntityData<T>;
                  return html`
                    <x-flex
                      center
                      horizontal
                      crossaxis-center
                      style="height:100%; width: 100%;"
                      id="target_${user._id}_${task._id}"
                      @click=${() =>
                        FeedbackPop.showPopup({
                          props: {
                            mode: this.objectiveName,
                            task: task,
                            selectedUsers: [[user._id, user.pseudoRealname, user.avgscore, user.userName]],
                            ...Object.fromEntries(
                              Object.entries(this.fullRecord.value[user._id][task._id]).map(([i, v]) => [
                                'popdata' + i,
                                v,
                              ]),
                            ),
                            popdatabestGoal: user.maxscoreinfo[0],
                          },
                        })}
                    >
                      <x-flex
                        data-microtip-position="top"
                        role="tooltip"
                        data-microtip-size="large"
                        aria-label="${`
                          ${
                            this.fullRecord.value[user._id][task._id]._id === undefined && this.objectiveName === 'task'
                              ? `Quiz:   
                              ${task.quizName} ` +
                                '\n \n' +
                                `Frage: ${shorten(JSON.stringify(task.taskQuestion), 200)}` +
                                ' \n \n' +
                                'Antwort: - ' +
                                ' \n \n' +
                                ' Score: 0%' +
                                ` ${
                                  this.objectiveName == 'task'
                                    ? {
                                        gapselect: ' (Lückentext)',
                                        essay: ' (Freitext)',
                                        multianswer: ' (Multiple Choice)',
                                        truefalse: ' (Multiple Choice)',
                                      }[task.taskType] || ''
                                    : ''
                                } \n *Aufgabe wurde nicht bearbeitet*`
                              : this.fullRecord.value[user._id][task._id]._id === undefined &&
                                this.objectiveName === 'goal'
                              ? `
                                ${
                                  this.realnames.value?.find(x => x._id === user.userName)?.realName ||
                                  user.pseudoRealname
                                } hat bisher keine Aufgaben zu diesem Lernziel abgegeben.` +
                                ' \n \n' +
                                ' Score: 0%'
                              : this.objectiveName === 'task' //this.fullRecord.value[user._id][task._id].taskType === 'essay'
                              ? `Quiz: ${task.quizName} \n \n Frage: ${shorten(
                                  JSON.stringify(this.fullRecord.value[user._id][task._id].taskQuestion),
                                  200,
                                )}` +
                                ' \n \n' +
                                `Antwort: ${
                                  this.fullRecord.value[user._id][task._id].response === null ||
                                  this.fullRecord.value[user._id][task._id].response === ''
                                    ? '-'
                                    : this.fullRecord.value[user._id][task._id].response
                                } \n \n Score: ${(this.fullRecord.value[user._id][task._id].score * 100).toFixed()}% ${
                                  this.objectiveName == 'task'
                                    ? {
                                        gapselect: ' (Lückentext)',
                                        essay: ' (Freitext)',
                                        multianswer: ' (Multiple Choice)',
                                        truefalse: ' (Multiple Choice)',
                                      }[this.fullRecord.value[user._id][task._id].taskType] || ''
                                    : ''
                                }
                                
                              ${
                                this.fullRecord.value[user._id][task._id].taskType === 'essay' &&
                                !(
                                  this.fullRecord.value[user._id][task._id].response === null ||
                                  this.fullRecord.value[user._id][task._id].response === ''
                                )
                                  ? `Score pro Wissenselement: \n ${
                                      this.fullRecord.value[user._id][task._id].nlp
                                        ?.map(
                                          x =>
                                            `${
                                              this.allGoals.value?.find(y => String(y._id) == String(x.goalId))?.[
                                                'goalTitle'
                                              ]
                                            }: ${(x.nlp * 100).toFixed()}%`,
                                        )
                                        .join(' \n ') || '*Aufgabe wurde nicht bearbeitet*'
                                    }`
                                  : this.fullRecord.value[user._id][task._id].taskType === 'essay' &&
                                    (this.fullRecord.value[user._id][task._id].response === null ||
                                      this.fullRecord.value[user._id][task._id].response === '')
                                  ? '*Aufgabe wurde nicht bearbeitet*'
                                  : ''
                              }`
                              : //essay
                                `Lernziel: ${
                                  this.fullRecord.value[user._id][task._id].goalTitle
                                } \n \n  Beschreibung: ${this.fullRecord.value[user._id][task._id].goalText}` +
                                ' \n \n' +
                                ` Score: ${(
                                  this.fullRecord.value[user._id][task._id].score * 100
                                ).toFixed()}% \n \n Klicke um Aufgaben einzusehen und Feedback zu geben. `
                          }
                          `}"
                        center
                        crossaxis-center
                        style="width:70px; ${this.objectiveName == 'goal'
                          ? 'height:20px;' //if different heights in goals needed
                          : 'height:20px;'} border-radius:3px; background: ${this.getChroma(
                          att.score,
                          att.taskType,
                          this.fullRecord.value[user._id][task._id]._id === undefined,
                          this.fullRecord.value[user._id][task._id].taskType === 'essay' &&
                            (this.fullRecord.value[user._id][task._id].response === null ||
                              this.fullRecord.value[user._id][task._id].response === ''),
                        )}; cursor:pointer;"
                      >
                        ${(this.courseSelected.value == '366' || this.courseSelected.value == '364') &&
                        this.objectiveName === 'goal'
                          ? att.score < 0.25
                            ? html`<x-icon type="material-outlined">close</x-icon>`
                            : att.score >= 0.25 && att.score < 0.5
                            ? html`<x-icon type="material-outlined">circle</x-icon>`
                            : att.score >= 0.5 && att.score < 0.75
                            ? html`<x-icon type="material-outlined">check</x-icon>`
                            : att.score >= 0.75
                            ? html`<x-icon type="material-outlined">check</x-icon>`
                            : ''
                          : ''}
                      </x-flex>
                    </x-flex>
                  `;
                })}
                <!-- Last column -->
                <x-flex horizontal crossaxis-center space="sm" style="width:100px;">
                  <x-icon
                    style="cursor:pointer; visibility: ${this.objectiveName === 'task' &&
                    this.taskcount.value >= 8 &&
                    (Object.values(this.fullRecord.value[user._id])
                      .filter(task => parseInt(task.moodleTaskId) <= 8)
                      .reduce((a, c) => a + c.score, 0) as number) < 1.5
                      ? 'visible'
                      : 'hidden'}; color:${colors.red}; font-size:15pt;"
                    data-microtip-position="top"
                    role="tooltip"
                    data-microtip-size="medium"
                    aria-label="${shorten(
                      this.realnames.value?.find(x => x._id === user.userName)?.realName || user.pseudoRealname,
                    ) +
                    ` hat nach Aufgabe 8 weniger als 20% der Aufgaben richtig gelöst. Dies kann dazu führen, dass ${shorten(
                      this.realnames.value?.find(x => x._id === user.userName)?.realName || user.pseudoRealname,
                    )} am Ende der Einheit im unteren viertel abschneidet.`}"
                  >
                    warning
                  </x-icon>
                  <x-text style="width:40px;">
                    <!-- ${(
                      ((Object.values(this.fullRecord.value[user._id]).reduce((a, c) => a + c.score, 0) as number) /
                        this.taskcount.value) *
                      100
                    ).toFixed()}% -->
                    ${(user.avgscore * 100).toFixed()}%
                    ${
                      ''
                      // (Object.values(this.fullRecord.value[user._id]).reduce((a, c) => a + c.score, 0) as number).toFixed(2)
                    }</x-text
                  >
                </x-flex>
              `,
            )}
          </x-grid>
          <!-- '#3b6fb1', '#8cc7e4', '#7BAFD4', '#eae97f' -> das ist das rot für task view: #477cc2c2-->
          <!-- LEGEND -->
          <x-flex space="md" horizontal>
            <x-text> Legende: </x-text>
            ${this.objectiveName === 'task'
              ? html`
                  <x-flex style="background:${this.isColorblind() ? '#477cc2c2' : colors.green}; width: 50px"></x-flex>
                  <x-text> korrekt </x-text>
                  <x-flex
                    style="${this.isColorblind()
                      ? 'margin-left: 50px; background:#eae97f'
                      : 'background:' + colors.red}; width: 50px"
                  ></x-flex>
                  <x-text> falsch </x-text>
                  ${this.isColorblind()
                    ? html``
                    : html`<x-flex style="background:${colors.orange}; width: 50px"></x-flex>
                        <x-text> teilweise korrekt </x-text>`}
                `
              : this.isColorblind()
              ? html` <x-flex style="background:#f1f1f1; width: 50px;align-items:center;"
                    ><x-icon type="material-outlined">close</x-icon></x-flex
                  >
                  <x-text style="margin:auto"> 0%-24% </x-text>
                  <x-flex style="background:#f1f1f1; width: 50px;align-items:center; "
                    ><x-icon type="material-outlined">circle</x-icon></x-flex
                  >
                  <x-text style="margin:auto"> 25%-49% </x-text>
                  <x-flex style="background:#f1f1f1; width: 50px;  align-items:center;"
                    ><x-icon type="material-outlined">check</x-icon></x-flex
                  >
                  <x-text style="margin:auto"> 50%-74% </x-text>
                  <x-flex style="background:#f1f1f1; width: 50px; align-items:center;"
                    ><x-icon type="material-outlined">check</x-icon></x-flex
                  >
                  <x-text style="margin:auto"> 75%-100% </x-text>`
              : html` <x-flex style="background:${colors.red}; width: 50px"></x-flex>
                  <x-text> 0%-24% </x-text>
                  <x-flex style="background:${colors.orange}; width: 50px"></x-flex>
                  <x-text> 25%-49% </x-text>
                  <x-flex style="background:${colors.yellow}; width: 50px"></x-flex>
                  <x-text> 50%-74% </x-text>
                  <x-flex style="background:${colors.green}; width: 50px"></x-flex>
                  <x-text> 75%-100% </x-text>`}
            ${this.isColorblind()
              ? ''
              : html`<x-flex style="background:${colors.gray}; width: 50px"></x-flex>
                  <x-text> nicht bearbeitet </x-text>`}
          </x-flex>

          <!-- BUTTONS -->
          <x-flex
            crossaxis-center
            space="md"
            style="padding: 10px; position: sticky; bottom: 20px; background: white; box-shadow: ${elevation[1]}; border-radius: 5px;"
          >
            <!-- Action buttons -->
            <x-flex space="md" crossaxis-center>
              <x-flex horizontal space="sm" crossaxis-center>
                ${this.totalPages.value === 1
                  ? nothing
                  : html`
                <mwc-icon-button
                  style="--mdc-icon-button-size:40px; --mdc-icon-size:40px;"
                  @click=${() => this.displayPage.next(this.displayPage.value > 1 ? this.displayPage.value - 1 : 1)}
                  icon="chevron_left"
                >
                </mwc-icon-button>
                <mwc-icon-button
                  icon="chevron_right"
                  style="--mdc-icon-button-size:40px; --mdc-icon-size:40px;"
                  @click=${() =>
                    this.displayPage.next(
                      this.displayPage.value < this.totalPages.value!
                        ? this.displayPage.value + 1
                        : this.totalPages.value!,
                    )}
                >
                </mwc-icon-button>
              </x-flex>`}
                <x-button
                  style="align-items:center; height:29px; padding-bottom:5px;"
                  icon="lightbulb"
                  ?disabled=${!this.selectedUsers.value.length || !this.users.value}
                  @click=${() => {
                    if (!this.selectedUsers.value.length || !this.users.value) return;
                    const selectedIds = this.selectedUsers.value.map(v => v.replace('checkbox-', ''));
                    const users = Object.entries(this.users.value).map(([i, v]) => [v._id, v.pseudoRealname]);
                    const selectedUsers = users.filter(u => selectedIds.includes(u[0]));
                    console.log('this.selectedUsers');
                    console.log(this.selectedUsers.value);
                    console.log('users');
                    console.log(this.users);
                    console.log();
                    const selectedFullUsers = this.selectedUsers.value.map(su =>
                      this.users.value?.filter(u => u._id == su),
                    );
                    FeedbackPop.showPopup({
                      props: {
                        mode: this.objectiveName,
                        selectedUsers: selectedUsers,
                        selectedFullUsers: selectedFullUsers,
                        feedbackFromCheckbox: true,
                      },
                    });
                  }}
                >
                  Feedback geben
                </x-button>
                ${this.objectiveName === 'goal'
                  ? html``
                  : //<x-button
                    //       @click="${() => {
                    //         this.toggleHigher.next(!this.toggleHigher.value);
                    //         new EventLog({ time: DateTime.now(), event: 'toggleHigherGoals' }).create();
                    //       }}"
                    //     >
                    //       Kompetenzziele anzeigen
                    //     </x-button>-->

                    html`
                      <x-dropdown-selection
                        style="padding-left:20px;"
                        .value=${'All'}
                        placeholder="Filter nach Lernzielen"
                        @value-changed=${(e: ValueEvent<{ title: string }>) => {
                          this.goalSelected.next(e.detail.value === 'All' ? null : e.detail.value);
                          console.log('CHANGED', e.detail.value);
                        }}
                        .selectables=${[null, ...(this.allGoals.value || [])]}
                        .renderString=${(goal: {
                          _id: string;
                          goalTitle: string;
                          goalText: string;
                          goalLevel: number;
                          avgscore: number;
                          taskIds: number[];
                        }) => {
                          console.log('goal filter', goal);
                          return goal === null ? 'All' : goal.goalTitle;
                        }}
                      ></x-dropdown-selection>
                    `}
                ${!this.userFilter.value.length
                  ? html``
                  : html`<x-chip
                      style="cursor: pointer;"
                      @click=${x => {
                        this.userFilter.next([] as string[]);
                        window.history.replaceState(
                          {},
                          document.title,
                          '/' +
                            window.location.hostname +
                            window.location.pathname +
                            window.location.search.split('&users=')[0],
                        );
                      }}
                    >
                      Filtering nach ${this.userFilter.value.length} Students
                    </x-chip>`}
              </x-flex>

              <!-- Last updated -->
              <!-- <x-text>
                Last updated:
                ${this.lastUpdate.value
                ? this.lastUpdate.value.toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS as string)
                : nothing}
              </x-text> -->
            </x-flex>
          </x-flex>
        </x-flex>
      `;
  }

  private isColorblind() {
    if (this.courseSelected.value == '364' || this.courseSelected.value == '366') {
      return true;
    }
  }
  private getChroma(score: number | null, taskType: string, notdone: boolean, essayDoneButEmpty: boolean) {
    // if (taskType === 'essay') {
    //   return colors.blue;
    // } else
    if ((this.courseSelected.value == '366' || this.courseSelected.value == '364') && this.objectiveName === 'goal') {
      return '#f1f1f1';
    }
    if (notdone) return colors.gray;
    if ((score === null || score === null) && taskType === 'essay' && !essayDoneButEmpty) {
      // That's the essay edge case where undefined is left over as the score from the aggregation. If all tasks have goal mappings, that won't happen
      return colors.red;
    }
    if ((score === null || score === null) && taskType === 'essay' && essayDoneButEmpty) {
      // That's the essay edge case where undefined is left over as the score from the aggregation. If all tasks have goal mappings, that won't happen
      return colors.gray;
    }
    let f;
    if (this.objectiveName === 'goal') {
      if (this.courseSelected.value == '366' || this.courseSelected.value == '364') {
        // f = chroma.scale(['#3b6fb1', '#8cc7e4', '#7BAFD4', '#eae97f']);

        // f = chroma.scale(['#ebe97f', '#ab9b03']);
        f = chroma.scale(['white']);
      } else {
        f = chroma.scale([colors.red, colors.yellow, colors.green]);
      }

      return f(score).css();
    } else if (this.objectiveName === 'task') {
      if (score == 0 || !score) {
        if (this.courseSelected.value == '366' || this.courseSelected.value == '364') {
          return '#eae97f';
        } else return colors.red;
      }
      if (score > 0 && score <= 0.6) {
        if (this.courseSelected.value == '366' || this.courseSelected.value == '364') {
          return '#477cc2c2';
        } else return colors.orange;
      }
      if (score > 0.6) {
        if (this.courseSelected.value == '366' || this.courseSelected.value == '364') {
          return '#477cc2c2';
        } else return colors.green;
      }
    }
  }

  private get_color(score: number | null, taskType: string) {
    if (taskType === 'essay') {
      //return colors.gray;
    } else if (score === null) {
      // return colors.gray;
    } else if (score < 0.25) {
      return colors.red;
    } else if (score < 0.5) {
      return colors.orange;
    } else if (score < 0.75) {
      return colors.yellow;
    } else if (score <= 1 && score >= 0.75) {
      return colors.green;
    } else {
      return 'black';
    }
  }
}

@customElement('task-table')
export class TaskTable extends TableView<typeof MoodleAttempts> {
  public collection = MoodleAttempts;
  public objectiveField = 'taskId';
  public objectiveName = 'task';
  public objectiveMeta = ['taskTitle', 'taskQuestion', 'taskType', 'linkAccordeon', 'moodleTaskId', 'quizName'];
}

@customElement('goal-table')
export class GoalTable extends TableView<typeof GoalAgg> {
  public collection = GoalAgg;
  public objectiveField = 'goalId';
  public objectiveName = 'goal';
  public objectiveMeta = ['goalTitle', 'goalText', 'goalLevel', 'taskIds'];
}
